import AdvancedDataTable from 'Components/Generic/AdvancedDataTable';
import DataTableHeaderTextFieldFilter from 'Components/Generic/AdvancedDataTable/DataTableHeaderTextFieldFilter';
import { TEXT_FILTER_MAP } from 'Components/Generic/AdvancedDataTable/Helper';
import OrganizationMultiselect from 'Components/Generic/BOSMultiselect/OrganizationMultiselect';
import BOSSelect from 'Components/Generic/BOSSelect';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import AddNewButton from 'Components/Generic/Buttons/AddNewButton';
import QuickViewButton from 'Components/Generic/Buttons/QuickViewButton';
import AccountDrawerButton from 'Components/SiteDrawerContainer/AccountDrawerButton';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import { SITE_PAGES } from 'Config';
import ChargeAccountData from 'Data/ChargeAccountData';
import { getOrgNameFromId } from 'Data/context/Organizations';
import { user_roles } from 'Data/Enum';
import SiteDrawerData from 'Data/SiteDrawerData';
import DataManager from 'Lib/DataManager';
import { usePersistentFiltering } from 'Lib/hooks/usePersistentFiltering';
import {
  getTransactionCategory,
  TRANSACTION_CATEGORY,
} from 'Pages/Transactions/Enum';
import { useEffect, useState } from 'react';
import { isUserAuthorized } from 'Util';
import { formatDate } from 'Util/dateUtil';
import AccountLanding from '../AccountLanding';
import AccountDetails from '../ChargeAccounts/AccountDetails';

let DM = new DataManager();

let filterOptions = {
  organization: {
    seqRestKey: '*parent_organization_id',
  },
  category: {
    seqRestKey: '*category',
  },
  org: {
    seqRestKey: '*org',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  fund: {
    seqRestKey: '*fund',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  code: {
    seqRestKey: '*code',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  name: {
    seqRestKey: '*name',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
};

const AccountListView = () => {
  const [data, setData] = useState();
  const [refreshKey, setRefreshKey] = useState(0);
  const [persistentFilter, updateFilterField] =
    usePersistentFiltering(`ACCOUNT_FILTER`);

  const onClick = () => {
    SiteDrawerData.open({
      content: drawerContent,
      title: 'New Charge Account',
    });
  };

  let addNewButton = isUserAuthorized({
    roles: [
      user_roles.ADMINISTRATOR,
      // [ '*', user_org_roles.MANAGER.key],
    ],
  }) && <AddNewButton buttonText='Add Charge Account' clickHandler={onClick} />;

  useEffect(() => {
    DM.add(
      ChargeAccountData.registerListener('LIST_LOADED', setData),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener(
        'LIST_LOAD_ERROR',
        onChargeAccountListLoadError,
      ),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener('RECORD_CREATED', onAccountCreated),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener('RECORD_UPDATED', refreshData),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener(
        'RECORDS_DELETED',
        refreshAndCloseDrawer,
      ),
      ChargeAccountData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  const onChargeAccountListLoadError = () => {};

  const onAccountCreated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Charge Account Created');
      refreshAndCloseDrawer();
    }
  };

  const refreshData = () => {
    setRefreshKey((prevState) => prevState + 1);
  };
  const refreshAndCloseDrawer = () => {
    refreshData();
    SiteDrawerData.close();
  };

  const headers = [
    {
      label: 'Core Facility',
      value: 'organization',
      fieldName: 'parent_organization_id',
      width: 180,
      sortable: true,
      sortFieldName: 'organizations.name',
      filterable: true,
      renderHeader() {
        return (
          <OrganizationMultiselect
            onSelectedOrganizationsUpdate={(orgList) =>
              updateFilterField('organization', orgList)
            }
            defaultSelectedItems={persistentFilter?.organization}
            isHeader
          />
        );
      },
      format: (orgId) => {
        return <OrgNameWithDrawerButton orgId={orgId} />;
      },
      getText: (account) => {
        return getOrgNameFromId(account.parent_organization_id);
      },
    },

    {
      label: 'Category',
      value: 'category',
      width: 100,
      filterable: true,
      renderHeader() {
        return (
          <BOSSelect
            listData={Object.entries(TRANSACTION_CATEGORY).map(
              ([key, value]) => {
                return {
                  id: key,
                  name: value,
                };
              },
            )}
            labelText='Category'
            useKeyValueFields
            onItemSelect={(item) => updateFilterField('category', [item])}
            defaultSelectedId={
              persistentFilter?.category?.length
                ? persistentFilter.category[0].id
                : undefined
            }
            isHeader
          />
        );
      },
      format: (category) => {
        return getTransactionCategory(category);
      },
    },

    {
      label: 'Org',
      value: 'org',
      sortable: true,
      width: 80,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Org'}
            defaultValue={persistentFilter?.org}
            onFilterValueUpdate={(e) =>
              updateFilterField('org', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Fund',
      value: 'fund',
      width: 80,
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Fund'}
            defaultValue={persistentFilter?.fund}
            onFilterValueUpdate={(e) =>
              updateFilterField('fund', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Act / Proj',
      value: 'code',
      width: 80,
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Act / Proj'}
            defaultValue={persistentFilter?.code}
            onFilterValueUpdate={(e) =>
              updateFilterField('code', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Name',
      value: 'name',
      sortable: true,
      filterable: true,
      truncated: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Name'}
            defaultValue={persistentFilter?.name}
            onFilterValueUpdate={(e) =>
              updateFilterField('name', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Start Date',
      value: 'valid_from',
      format: formatDate,
      width: 120,
      sortable: true,
    },

    {
      label: 'End Date',
      value: 'valid_to',
      format: formatDate,
      width: 120,
      sortable: true,
    },

    {
      label: '',
      value: 'quick_view',
      isIconCol: true,
      render(ca) {
        return (
          <AccountDrawerButton
            accountId={ca.id}
            drawerTitle={`${ca.short_name || ca.name}: ${ca.code}`}
            label={<QuickViewButton />}
          />
        );
      },
    },
  ];

  let drawerContent = (
    <AccountDetails hideToggle editable parentEditState={true} />
  );

  let breadcrumb = [SITE_PAGES.ALL_ACCOUNTS];

  let pageContent = (
    <AdvancedDataTable
      headers={headers}
      refreshKey={refreshKey}
      title={'All Accounts'}
      dataKey={'ACCOUNT'}
      data={data}
      dataQueryFunc={ChargeAccountData.getChargeAccountList}
      filterOptions={filterOptions}
      filterBy={persistentFilter}
      updateFilterField={updateFilterField}
      defaultSort={'organizations.name'}
      actionButtons={addNewButton}
    />
  );

  return (
    <AccountLanding breadcrumbs={breadcrumb}>{pageContent}</AccountLanding>
  );
};

export default AccountListView;
